/**
 * しおり・ふせんの一覧パネル（EP-1）。
 *
 * このブックに付いている目印をページ昇順で並べ、選ぶとそのページへ移動し、
 * 外すボタンで削除する。データ源はビューワが持つキャッシュ（`api.listMarks`）で、
 * ここからネットワークは触らない。
 */
import { cls } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { ViewerState } from '../core/viewer';
import type { Translate } from '../i18n/i18n';
import type { MarkType, UserItem } from '../user-data/store';
import type { ViewerApi } from './api';
import { ICONS } from './icons';

/** パネルに並べる種別（本棚はブック単位なのでここには出さない）。 */
const PAGE_MARKS: readonly MarkType[] = ['bookmark', 'sticky'];

export class MarksPanel {
  readonly el: HTMLDivElement;

  private readonly list: HTMLUListElement;
  private readonly empty: HTMLParagraphElement;

  constructor(
    private readonly doc: Document,
    private readonly registry: DisposeRegistry,
    private readonly api: ViewerApi,
    private readonly translate: Translate,
  ) {
    this.el = doc.createElement('div');
    this.el.className = cls('marks');

    this.empty = doc.createElement('p');
    this.empty.className = cls('marks-empty');
    this.empty.textContent = translate('marks.empty');

    this.list = doc.createElement('ul');
    this.list.className = cls('marks-list');

    this.el.append(this.empty, this.list);
  }

  /** パネルが開いたとき。 */
  activate(): void {
    this.render();
  }

  /** 状態変化（ページ移動・目印の付け外し）に追従する。 */
  sync(state: ViewerState): void {
    void state;
    this.render();
  }

  private render(): void {
    const entries: { type: MarkType; item: UserItem }[] = [];
    for (const type of PAGE_MARKS) {
      for (const item of this.api.listMarks(type)) entries.push({ type, item });
    }
    entries.sort((a, b) => (a.item.page ?? 0) - (b.item.page ?? 0));

    this.empty.hidden = entries.length > 0;
    const fragment = this.doc.createDocumentFragment();
    for (const entry of entries) fragment.appendChild(this.row(entry.type, entry.item));
    this.list.replaceChildren(fragment);
  }

  private row(type: MarkType, item: UserItem): HTMLLIElement {
    const page = item.page ?? 1;
    const li = this.doc.createElement('li');
    li.className = cls('mark');

    const jump = this.doc.createElement('button');
    jump.type = 'button';
    jump.className = cls('mark-jump');

    const kind = this.doc.createElement('span');
    kind.className = `${cls('mark-kind')} ${cls(`mark-kind--${type}`)}`;
    kind.innerHTML = ICONS[type] ?? '';

    const label = this.doc.createElement('span');
    label.className = cls('mark-label');
    label.textContent = this.translate('marks.page', { page: this.api.pageLabel(page) });

    jump.append(kind, label);
    jump.setAttribute(
      'aria-label',
      `${this.translate(`marks.${type}`)} — ${this.translate('marks.page', { page: this.api.pageLabel(page) })}`,
    );
    this.registry.listen(jump, 'click', () => this.api.goto(page));

    const remove = this.doc.createElement('button');
    remove.type = 'button';
    remove.className = `${cls('button')} ${cls('mark-remove')}`;
    remove.innerHTML = ICONS.close ?? '';
    const removeLabel = this.translate('marks.remove');
    remove.setAttribute('aria-label', removeLabel);
    remove.title = removeLabel;
    this.registry.listen(remove, 'click', () => this.api.removeMark(type, page));

    li.append(jump, remove);
    return li;
  }
}
